import { TARS_PIVOT_TOTAL_LABEL } from "../../tars-constants";

export interface FacetTableData {
    headers: string[];
    rows: Record<string, unknown>[];
}

function extractHeaders(input: unknown): string[] {
    if (!Array.isArray(input)) return [];
    return input
        .map((x) => {
            if (typeof x === "string") return x;
            if (x != null && typeof x === "object") {
                const obj = x as Record<string, unknown>;
                const key = obj.key ?? obj.name ?? obj.header ?? obj.label;
                return typeof key === "string" ? key : null;
            }
            return null;
        })
        .filter((x): x is string => Boolean(x));
}

function extractRows(input: unknown): Record<string, unknown>[] {
    if (!Array.isArray(input)) return [];
    return input.filter(
        (x): x is Record<string, unknown> => x != null && typeof x === "object" && !Array.isArray(x)
    );
}

function normalizeRows(headers: string[], input: unknown): Record<string, unknown>[] {
    const objectRows = extractRows(input);
    if (objectRows.length > 0) return objectRows;
    if (!Array.isArray(input) || headers.length === 0) return [];
    const arrayRows = input.filter((r): r is unknown[] => Array.isArray(r));
    if (arrayRows.length === 0) return [];
    return arrayRows.map((cells) => {
        const row: Record<string, unknown> = {};
        headers.forEach((header, idx) => {
            row[header] = cells[idx] ?? "";
        });
        return row;
    });
}

export function extractFacetTableData(facetResult: unknown): FacetTableData | null {
    if (facetResult == null || typeof facetResult !== "object") return null;
    const obj = facetResult as Record<string, unknown>;
    const explicitHeaders = extractHeaders(obj.col_header);
    if (explicitHeaders.length === 0) return null;

    const rowsCandidatesRaw = [obj.rows, obj.data, obj.records, obj.items];
    const normalizedRows =
        rowsCandidatesRaw
            .map((rows) => normalizeRows(explicitHeaders, rows))
            .find((r) => r.length > 0) ?? [];

    if (normalizedRows.length === 0) return null;

    return { headers: explicitHeaders, rows: normalizedRows };
}

/** Column keys for pivot — `facet_result.col_header`. */
export function facetColHeaderOptions(facetResult: unknown): string[] {
    if (facetResult == null || typeof facetResult !== "object") return [];
    return extractHeaders((facetResult as Record<string, unknown>).col_header)
        .map((h) => h.trim());
}

function rowLabelsFromFacetRows(facetResult: Record<string, unknown>): string[] {
    const rows = facetResult.rows;
    if (!Array.isArray(rows)) return [];
    const labels: string[] = [];
    for (const row of rows) {
        if (Array.isArray(row) && row.length > 0) {
            labels.push(String(row[0] ?? "").trim());
        }
    }
    return [...new Set(labels)];
}

function isMatrixFacetResult(facetResult: Record<string, unknown>): boolean {
    const rows = facetResult.rows;
    return Array.isArray(rows) && rows.some((r) => Array.isArray(r) && r.length > 2);
}

/** Matrix tables have 3+ columns; 1D pivot tables have exactly 2 (label + value). */
export function isMatrixTableData(table: FacetTableData | null | undefined): boolean {
    return (table?.headers.length ?? 0) > 2;
}

/** Column labels for transform / whitelist — uses transformed `col_header` (matrix: skips row label). */
export function facetTransformColOptions(facetResult: unknown): string[] {
    if (facetResult == null || typeof facetResult !== "object") return [];
    const obj = facetResult as Record<string, unknown>;
    const headers = extractHeaders(obj.col_header).map((h) => h.trim());
    if (headers.length === 0) return [];
    if (isMatrixFacetResult(obj)) {
        return [...new Set(headers.slice(1).filter((h) => h && h !== TARS_PIVOT_TOTAL_LABEL))];
    }
    return [...new Set(headers)];
}

function normalizeStringList(list: unknown[]): string[] {
    return [...new Set(list.map((x) => String(x ?? "").trim()).filter(Boolean))];
}

function rowHeaderListForKey(obj: Record<string, unknown>, keyHint: string): string[] | null {
    const hint = keyHint.trim();
    if (!hint) return null;
    for (const field of ["row_headers", "row_header"] as const) {
        const raw = obj[field];
        const list = Array.isArray(raw)
            ? raw
            : raw != null && typeof raw === "object"
              ? (raw as Record<string, unknown>)[hint]
              : null;
        if (Array.isArray(list)) {
            return normalizeStringList(list);
        }
    }
    return null;
}

/**
 * Row axis labels for transform / blacklist / whitelist.
 * Prefers live table `rows[*][0]` (post-transform labels), then legacy `row_headers`.
 */
export function facetTableRowValueOptions(facetResult: unknown, rowKeyHint: string): string[] {
    if (facetResult == null || typeof facetResult !== "object") return [];
    const obj = facetResult as Record<string, unknown>;
    const fromRows = rowLabelsFromFacetRows(obj);
    if (fromRows.length > 0) return fromRows;
    return rowHeaderListForKey(obj, rowKeyHint) ?? [];
}
